
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Teacher {
  id: string;
  code: string;
  name: string;
  label: string;
}

export const useTeacherSearch = (teacherCode?: string) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const search = useCallback(async (query: string) => {
    if (!query || query.length < 2) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("search-teachers", {
        body: { query }
      });
      
      if (error) {
        console.error("Error fetching teachers:", error);
        toast({
          title: "Error",
          description: "Could not fetch teacher data. Please try again.",
          variant: "destructive",
        });
        setTeachers([]);
        return;
      }

      // Make sure each teacher has id, code, name, and label properties
      const formattedTeachers = (data?.teachers || []).map(teacher => ({
        id: teacher.id || teacher.code,
        code: teacher.code,
        name: teacher.name,
        label: teacher.label || `${teacher.code} - ${teacher.name}`
      }));

      setTeachers(formattedTeachers);
    } catch (error) {
      console.error("Error in teacher search:", error);
      toast({
        title: "Error",
        description: "Connection error. Please check your network.",
        variant: "destructive",
      });
      setTeachers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch teachers when search query changes (debounced)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setTeachers([]);
      return;
    }

    const timer = setTimeout(() => {
      search(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, search]);

  // If teacherCode is provided, search for that specific teacher
  useEffect(() => {
    if (teacherCode) {
      search(teacherCode);
    }
  }, [teacherCode, search]);

  return {
    searchQuery,
    setSearchQuery,
    teachers,
    isLoading,
    search
  };
};
